import { useState } from "react";
import { toast } from "react-toastify";
import { useRouter } from "next/router";
import { useMutation } from "@tanstack/react-query";

import { useAppDispatch } from "../store";
import { userState } from "../store/data/user";
import { useTeamConfig } from "./useTeamConfig";
import { useToggle } from "@/shared/base/hooks/useToggle";

import {
  LoginApi,
  RegisterApi,
  forgotPasswordApi,
  resetPasswordApi,
} from "../api/auth";
import {
  authUIDs,
  authOriginUIDs,
  purchaseSeatsTabUIDs,
} from "../config/constants";
import { routes } from "../routes";
import { parseApiErrorMessage } from "../utils/utils";
import { globalConfig } from "../config/global_config";
import { createBase64String, decodeBase64String } from "../utils/auth.utils";
import { handleRemoveUpdatedAccountGroup } from "@/shared/modules/PurchaseSeats/Packs/packs.utils";

import type {
  StateDecoded,
  InternalLoginProps,
  RegisterInternalProps,
} from "@/types";
import type { AuthCallback } from "./useTicketmaster";

export function useInternalAuth() {
  /** router states or callbacks */
  const { push, query } = useRouter();
  const email = query.email as string;
  const token = query.token as string;
  const callback = decodeBase64String<AuthCallback<any>>(
    query.callback as string
  );
  const decodedState = decodeBase64String<StateDecoded>(query.state as string);

  /** mutations */
  const loginMutation = useMutation(LoginApi());
  const registerLinkTmAccount = useMutation(RegisterApi());
  const forgotPasswordMutation = useMutation(forgotPasswordApi);
  const resetPasswordMutation = useMutation(resetPasswordApi);

  /** redux and states */
  const dispatch = useAppDispatch();
  const successModal = useToggle();
  const loginErrorModal = useToggle();
  const frontendUrl = globalConfig.frontendUrl;
  const {
    teamSchema,
    defaultRoute,
    shouldUseInternalLogin,
    oktaClientId,
    oktaIssuer,
  } = useTeamConfig();
  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [redirectLoading, setRedirectLoading] = useState(false);

  function handleInternalLogOut() {
    const redirectUri = `${frontendUrl}/${teamSchema}`;
    dispatch(userState.actions.remove({ teamSchema }));

    if (!!oktaClientId && !!oktaIssuer) {
      window.localStorage.removeItem("okta-token-storage");
      window.localStorage.removeItem("okta-cache-storage");
      window.localStorage.removeItem("okta-original-uri-storage");
      window.localStorage.removeItem("okta-shared-transaction-storage");
    }

    handleRemoveUpdatedAccountGroup(teamSchema);
    return window.open(redirectUri, "_self");
  }

  async function handleRegisterInternal(data: RegisterInternalProps) {
    if (!shouldUseInternalLogin) return;

    try {
      const { confirmPassword, termsAndConditions, ...rest } = data;
      const payload = {
        ...rest,
        teamSchema,
        shouldUseInternalLogin,
        email: email || (data.email as string),
      };

      return await registerLinkTmAccount.mutateAsync(payload, {
        onSuccess: async (res) => {
          dispatch(userState.actions.set({ teamSchema, session: res?.data }));

          // generic auth redirect handling
          if (callback) {
            const query = Object.create(null);
            query.teamSchema = teamSchema;
            if (callback?.payload)
              query.payload = createBase64String(callback?.payload);
            return await push({ pathname: callback?.pathname, query });
          }

          if (decodedState) {
            if (!!decodedState?.callback) {
              return await push({
                pathname: decodedState?.callback?.pathname,
                query: {
                  teamSchema,
                  payload: createBase64String(decodedState?.callback?.payload),
                },
              });
            }

            return await push({
              pathname: decodedState?.pathname,
              query: decodedState?.query,
            });
          }

          if (query.authOrigin === authOriginUIDs.purchaseCredit) {
            return await push({
              pathname: routes.purchaseSeats,
              query: {
                teamSchema,
                payload: query?.payload,
              },
            });
          }

          if (query.eventCodes) {
            let routerQuery: any = {
              purchaseSeatTab: purchaseSeatsTabUIDs.chooseSeats,
              teamSchema,
              eventCodes: query.eventCodes,
            };

            if (query.packageId) routerQuery.packageId = query.packageId;
            return await push({
              pathname: routes.purchaseSeats,
              query: routerQuery,
            });
          }

          return await push({
            pathname: defaultRoute,
            query: {
              teamSchema,
            },
          });
        },
        onError: (data: any) => {
          toast.error(data?.response?.data?.error);
        },
      });
    } catch (error: any) {
      console.log(error.response.data.error);
    }
  }

  async function handleInternalLogin(data: InternalLoginProps) {
    try {
      if (!shouldUseInternalLogin) return;

      const { email, password } = data;

      const loginResponse = await loginMutation.mutateAsync({
        email,
        password,
        teamSchema,
        shouldUseInternalLogin,
      });

      if (!!decodedState?.callback) {
        if (loginResponse?.data?.register) {
          return await push({
            pathname: routes.auth,
            query: {
              teamSchema,
              authType: authUIDs.register,
              callback: createBase64String(decodedState.callback),
              email: loginResponse?.data?.register?.email,
              accountId: loginResponse?.data?.register?.accountId,
            },
          });
        } else {
          dispatch(
            userState.actions.set({ session: loginResponse?.data, teamSchema })
          );
          return await push({
            pathname: decodedState?.callback?.pathname,
            query: {
              teamSchema,
              payload: createBase64String(decodedState?.callback?.payload),
            },
          });
        }
      }

      if (decodedState?.authOrigin == authOriginUIDs.generalAuth) {
        if (loginResponse?.data?.register) {
          const { email, accountId } = loginResponse?.data?.register;
          return await push({
            pathname: routes.auth,
            query: {
              authType: authUIDs.register,
              teamSchema,
              email,
              accountId,
            },
          });
        } else {
          dispatch(
            userState.actions.set({ session: loginResponse?.data, teamSchema })
          );
          return await push({
            pathname: decodedState.pathname,
            query: decodedState.query,
          });
        }
      }

      if (decodedState?.authOrigin === authOriginUIDs.purchaseSeats) {
        if (loginResponse?.data?.register) {
          const { email, accountId } = loginResponse?.data?.register;
          let query: any = {
            authType: authUIDs.register,
            teamSchema,
            email,
            accountId,
            eventCodes: decodedState?.query?.eventCodes,
          };
          if (decodedState?.query?.packageId)
            query.packageId = decodedState.query.packageId;
          return await push({
            pathname: routes.auth,
            query,
          });
        } else {
          dispatch(
            userState.actions.set({ session: loginResponse?.data, teamSchema })
          );
          return await push({
            pathname: decodedState.pathname,
            query: decodedState.query,
          });
        }
      }

      if (decodedState?.authOrigin === authOriginUIDs.loginPage) {
        if (loginResponse?.data?.register) {
          const { email, accountId } = loginResponse?.data?.register;
          return await push({
            pathname: routes.auth,
            query: {
              authType: authUIDs.register,
              teamSchema,
              email,
              accountId,
            },
          });
        } else {
          dispatch(
            userState.actions.set({ session: loginResponse?.data, teamSchema })
          );
          return await push({
            pathname: decodedState.pathname,
            query: decodedState.query,
          });
        }
      }

      // generic auth redirect handling
      if (loginResponse?.data?.register) {
        return await push({
          pathname: routes.auth,
          query: {
            teamSchema,
            authType: authUIDs.register,
            email: loginResponse?.data?.register?.email,
            eventCodes: query?.eventCodes ?? undefined,
          },
        });
      } else {
        dispatch(
          userState.actions.set({ session: loginResponse.data, teamSchema })
        );
        return await push({
          pathname: routes.purchaseSeats,
          query: {
            purchaseSeatTab: purchaseSeatsTabUIDs.findGames,
            teamSchema,
          },
        });
      }
    } catch (error: any) {
      let errorMessage = parseApiErrorMessage(error);
      setLoginErrorMessage(errorMessage);
      loginErrorModal.onOpen();
    }
  }

  function constructCallbackUrl() {
    switch (true) {
      case !!callback:
        return createBase64String(callback);

      case !!decodedState:
        return createBase64String(decodedState);

      default:
        undefined;
    }
  }

  async function handleForgotPassword(data: { email: string }) {
    try {
      const payload = {
        teamSchema,
        email: data.email,
        callback: constructCallbackUrl(),
      };

      const forgotPasswordResponse = await forgotPasswordMutation.mutateAsync(
        payload
      );

      setSuccessMessage(forgotPasswordResponse?.data?.message);
      successModal.onOpen();
    } catch (error: any) {
      let errorMessage = parseApiErrorMessage(error);
      setLoginErrorMessage(errorMessage);
      loginErrorModal.onOpen();
    }
  }

  async function handleResetPassword(data: {
    password: string;
    confirmPassword: string;
  }) {
    try {
      const payload = {
        email,
        teamSchema,
        resetToken: token,
        password: data.password,
      };
      const resetPasswordResponse = await resetPasswordMutation.mutateAsync(payload); // prettier-ignore
      setSuccessMessage(resetPasswordResponse?.data?.message);
      successModal.onOpen();
    } catch (error: any) {
      let errorMessage = parseApiErrorMessage(error);
      setLoginErrorMessage(errorMessage);
      loginErrorModal.onOpen();
    }
  }

  // prettier-ignore
  async function handleInternalRedirect(pathname: string, state: string, authType: string) {
    try {
      setRedirectLoading(true);
      return await push({
        pathname,
        query: {
          state,
          teamSchema,
          authType,
          eventCodes: query.eventCodes ?? undefined,
        },
      });
    } catch (err: any) {
      console.log(err)
    } finally {
      setRedirectLoading(false);
    }
  }

  async function handleRedirectToLogin() {
    const state = query?.state as string;
    return await handleInternalRedirect(routes.auth, state, authUIDs.login);
  }

  return {
    successModal,
    successMessage,
    loginErrorModal,
    redirectLoading,
    loginErrorMessage,
    handleInternalLogin,
    handleResetPassword,
    handleInternalLogOut,
    handleForgotPassword,
    handleRedirectToLogin,
    handleInternalRedirect,
    handleRegisterInternal,
    shouldUseInternalLogin,
    internalLoginLoading: loginMutation.isLoading,
    resetPasswordLoading: resetPasswordMutation.isLoading,
    forgotPasswordLoading: forgotPasswordMutation.isLoading,
    internalRegisterLoading: registerLinkTmAccount.isLoading,
  };
}
